<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui'
import { useRecentPages } from '../composables/useRecentPages'

/**
 * Jump-to palette. `entries`: [{ key, label, section, route, icon?, badge? }].
 * Emits `select` with the chosen entry; the parent decides how to navigate.
 */
const props = defineProps({
  entries: { type: Array, default: () => [] },
})
const open = defineModel('open', { type: Boolean, default: false })
const emit = defineEmits(['select'])

const { pinned, recent, togglePin, isPinned } = useRecentPages()

const query = ref('')
const active = ref(0)
const listEl = ref(null)
const inputRef = ref(null)

const results = computed(() => {
  const q = query.value.trim().toLowerCase()
  const scored = props.entries
    .map((entry) => {
      if (!q) return { entry, score: 0, match: true }
      const label = entry.label.toLowerCase()
      const haystack = `${label} ${(entry.section || '').toLowerCase()}`
      if (!haystack.includes(q)) return { entry, score: 3, match: false }
      return { entry, score: label.startsWith(q) ? 0 : label.includes(q) ? 1 : 2, match: true }
    })
    .filter((row) => row.match)

  const pinnedRoutes = new Set(pinned.value.map((item) => item.route))
  const recentRoutes = new Set(recent.value.map((item) => item.route))
  scored.sort(
    (a, b) =>
      Number(pinnedRoutes.has(b.entry.route)) - Number(pinnedRoutes.has(a.entry.route)) ||
      Number(recentRoutes.has(b.entry.route)) - Number(recentRoutes.has(a.entry.route)) ||
      a.score - b.score,
  )
  return scored.slice(0, 60).map((row) => row.entry)
})

watch(open, (value) => {
  if (value) {
    query.value = ''
    active.value = 0
    nextTick(() => inputRef.value?.focus())
  }
})

watch(query, () => {
  active.value = 0
})

watch(active, async () => {
  await nextTick()
  listEl.value?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
})

function choose(entry) {
  if (!entry) return
  open.value = false
  emit('select', entry)
}

function onKeydown(event) {
  const count = results.value.length
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (count) active.value = (active.value + 1) % count
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (count) active.value = (active.value - 1 + count) % count
  } else if (event.key === 'Home') {
    event.preventDefault()
    active.value = 0
  } else if (event.key === 'End') {
    event.preventDefault()
    active.value = Math.max(count - 1, 0)
  } else if (event.key === 'Enter') {
    event.preventDefault()
    choose(results.value[active.value])
  }
}
</script>

<template>
  <DialogRoot v-model:open="open">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-[125] bg-black/40" />
      <DialogContent
        class="fixed left-1/2 top-[12vh] z-[130] w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-outline-gray-1 bg-surface-elevation-2 shadow-2xl outline-none"
        data-testid="command-palette"
      >
        <DialogTitle class="sr-only">Search and jump to</DialogTitle>
        <DialogDescription class="sr-only">
          Type to filter pages and actions. Use the arrow keys to move and Enter to open.
        </DialogDescription>

        <div class="flex items-center gap-2 border-b border-outline-gray-1 px-3">
          <span class="lucide-search size-4 shrink-0 text-ink-gray-5" aria-hidden="true" />
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-list"
            :aria-activedescendant="results.length ? `command-option-${active}` : undefined"
            aria-label="Search pages and actions"
            placeholder="Search pages and actions"
            autocomplete="off"
            class="h-12 min-w-0 flex-1 border-0 bg-transparent text-base text-ink-gray-9 placeholder:text-ink-gray-5 focus:outline-none focus:ring-0"
            @keydown="onKeydown"
          />
          <kbd class="hidden rounded border border-outline-gray-2 px-1.5 py-0.5 text-[11px] text-ink-gray-6 sm:inline">
            Esc
          </kbd>
        </div>

        <ul
          id="command-palette-list"
          ref="listEl"
          role="listbox"
          aria-label="Results"
          class="max-h-[50vh] overflow-y-auto p-1.5"
        >
          <li
            v-for="(entry, index) in results"
            :id="`command-option-${index}`"
            :key="entry.key"
            role="option"
            :aria-selected="index === active"
            class="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-ink-gray-8"
            :class="index === active ? 'bg-surface-gray-3' : ''"
            @mousemove="active = index"
            @click="choose(entry)"
          >
            <span :class="[entry.icon || 'lucide-corner-down-right', 'size-4 shrink-0 text-ink-gray-5']" aria-hidden="true" />
            <span class="min-w-0 flex-1 truncate">{{ entry.label }}</span>
            <span
              v-if="entry.badge"
              class="shrink-0 rounded-full bg-surface-red-2 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-ink-red-7"
            >
              {{ entry.badge > 99 ? '99+' : entry.badge }}
            </span>
            <span class="hidden shrink-0 text-xs text-ink-gray-5 sm:inline">{{ entry.section }}</span>
            <button
              type="button"
              class="shrink-0 rounded p-1 text-ink-gray-4 transition hover:bg-surface-gray-1 hover:text-ink-gray-7"
              :aria-label="isPinned(entry.route) ? `Unpin ${entry.label}` : `Pin ${entry.label}`"
              :title="isPinned(entry.route) ? 'Unpin' : 'Pin'"
              @click.stop="togglePin(entry.route)"
            >
              <span
                class="lucide-star size-4"
                :class="isPinned(entry.route) ? 'text-amber-500' : ''"
                :style="isPinned(entry.route) ? 'fill: currentColor' : ''"
                aria-hidden="true"
              />
            </button>
          </li>
          <li v-if="!results.length" class="px-3 py-8 text-center text-sm text-ink-gray-6" role="presentation">
            No pages match “{{ query }}”. Try a shorter word.
          </li>
        </ul>

        <div
          class="hidden items-center gap-4 border-t border-outline-gray-1 px-3 py-2 text-xs text-ink-gray-5 sm:flex"
          aria-hidden="true"
        >
          <span>↑↓ to move</span>
          <span>Enter to open</span>
          <span>★ to pin</span>
          <span>Esc to close</span>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
